import { supabase } from '../lib/supabaseClient';
import type {
  MigrationExceptionRecord,
  MigrationMapping,
  MigrationReconciliationRow,
  MigrationRunDetail,
  MigrationRunSummary,
  UUID,
} from '../types/domain';

/**
 * Migration Rehearsal (FASE 15, migrations 0059-0061). Dry-run ETL into
 * isolated clean-target tenants — never the live operational tables.
 * Every mutation below goes through a permission-checked RPC
 * (migration.operate / migration.approve / migration.signoff); reads
 * require migration.read. All four are TENANT_OWNER-only server-side.
 */

export async function listMigrationRuns(): Promise<MigrationRunSummary[]> {
  const { data, error } = await supabase.rpc('fn_migration_list_runs_checked');
  if (error) throw new Error(`fn_migration_list_runs_checked failed: ${error.message}`);
  return (data ?? []) as MigrationRunSummary[];
}

export async function getMigrationRunDetail(migrationRunId: UUID): Promise<MigrationRunDetail> {
  const { data, error } = await supabase.rpc('fn_migration_get_run_detail_checked', { p_migration_run_id: migrationRunId });
  if (error) throw new Error(`fn_migration_get_run_detail_checked failed: ${error.message}`);
  return data as MigrationRunDetail;
}

export async function getMigrationReconciliation(migrationRunId: UUID): Promise<MigrationReconciliationRow[]> {
  const { data, error } = await supabase.rpc('fn_migration_get_reconciliation_checked', { p_migration_run_id: migrationRunId });
  if (error) throw new Error(`fn_migration_get_reconciliation_checked failed: ${error.message}`);
  return (data ?? []) as MigrationReconciliationRow[];
}

export async function confirmMigrationMapping(mappingId: UUID, decision: 'CONFIRMED' | 'REJECTED', resolvedValue: string | null): Promise<void> {
  const { error } = await supabase.rpc('fn_migration_confirm_mapping_checked', {
    p_mapping_id: mappingId,
    p_decision: decision,
    p_resolved_value: resolvedValue,
  });
  if (error) throw new Error(`fn_migration_confirm_mapping_checked failed: ${error.message}`);
}

/** AI-assist only: writes candidate suggestions, never resolves the mapping itself. */
export async function suggestMigrationMapping(mappingId: UUID): Promise<{ candidate_suggestions: string[] }> {
  const { data, error } = await supabase.rpc('fn_migration_suggest_mapping_checked', { p_mapping_id: mappingId });
  if (error) throw new Error(`fn_migration_suggest_mapping_checked failed: ${error.message}`);
  return data as { candidate_suggestions: string[] };
}

export async function commitMigrationRun(migrationRunId: UUID): Promise<{ status: string; imported_count: number; idempotent_replay: boolean }> {
  const { data, error } = await supabase.rpc('fn_migration_commit_checked', { p_migration_run_id: migrationRunId });
  if (error) throw new Error(`fn_migration_commit_checked failed: ${error.message}`);
  return data as { status: string; imported_count: number; idempotent_replay: boolean };
}

export async function signoffMigrationException(exceptionId: UUID, disposition: string): Promise<void> {
  const { error } = await supabase.rpc('fn_migration_signoff_exception_checked', { p_exception_id: exceptionId, p_disposition: disposition });
  if (error) throw new Error(`fn_migration_signoff_exception_checked failed: ${error.message}`);
}

/**
 * Correction path for an exception that is already SIGNED_OFF (migration
 * 0062) — e.g. replacing a rehearsal-only signer with the real owner. Never
 * overwrites the original sign-off silently: the server preserves the first
 * signer/timestamp/disposition and records a before/after audit_events row
 * every time. `idempotencyKey` must be a stable UUID for one correction
 * intent — a repeated call with the same key is a no-op replay.
 */
export async function correctMigrationExceptionSignoff(
  exceptionId: UUID,
  disposition: string,
  idempotencyKey: UUID
): Promise<{ idempotent_replay: boolean }> {
  const { data, error } = await supabase.rpc('fn_migration_correct_signoff_exception_checked', {
    p_exception_id: exceptionId,
    p_disposition: disposition,
    p_idempotency_key: idempotencyKey,
  });
  if (error) throw new Error(`fn_migration_correct_signoff_exception_checked failed: ${error.message}`);
  return data as { idempotent_replay: boolean };
}

export async function createRollbackCheckpoint(migrationRunId: UUID): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_migration_create_rollback_checkpoint_checked', { p_migration_run_id: migrationRunId });
  if (error) throw new Error(`fn_migration_create_rollback_checkpoint_checked failed: ${error.message}`);
  return data as UUID;
}

export async function rollbackMigrationCheckpoint(checkpointId: UUID): Promise<void> {
  const { error } = await supabase.rpc('fn_migration_rollback_checked', { p_checkpoint_id: checkpointId });
  if (error) throw new Error(`fn_migration_rollback_checked failed: ${error.message}`);
}

export type { MigrationExceptionRecord, MigrationMapping };
